"use client";

import { Link, usePathname, useRouter } from "@/i18n/navigation";
import NewLinkNave from "../../NewLinkNav/NewLinkNave";
import Logo from "../../Logo/Logo";
import PinkOctober from "@/components/ui/PinkOctober/PinkOctober";
import { useEffect, useState } from "react";
import Level from "../../Gamification/Level/Level";
import Button from "../../Button/Button";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import { useTranslations, useLocale } from "next-intl";
import { routing, labels } from "@/i18n/routing";
import { useParams, usePathname as useRawPathname } from "next/navigation";
import { TeamSwitcher } from "@/components/Teams/TeamSwitcher";
import calculatePlayerLevel from "@/helpers/calculatePlayerLevel";
import { syncAccountLanguage } from "@/helpers/syncAccountLanguage";
import ContactModal from "@/components/ContactModal/ContactModal";
import FeatureSuggestion from "@/components/FeatureSuggestion/FeatureSuggestion";

export default function SideHeader({
    page,
    session,
    sessionLoading,
    sideNavIsOpen = false,
    promoConfig: PROMO_CONFIG = {},
    onCollapse,
    compact = false,
    onExpand,
}) {
    const t = useTranslations("layout");
    const [helpModalIsOpen, setHelpModalIsOpen] = useState(false);
    const [contactIsOpen, setContactIsOpen] = useState(false);
    const [languagesModalIsOpen, setLanguagesModalIsOpen] = useState(false);
    const pathname = usePathname();
    const rawPathname = useRawPathname();
    const params = useParams();
    const router = useRouter();
    const locale = useLocale();

    const updateLanguage = async (language) => {
        if (language == locale) return;

        // Changer de langue est un geste explicite : pour un utilisateur
        // connecté, on aligne la langue du compte (e-mails, notifications).
        if (session?.user) {
            syncAccountLanguage(language);
        }

        try {
            router.push({ pathname, params }, { locale: language });
        } catch (error) {
            // Filet de sécurité : certaines pages statiques (ex. formations) correspondent
            // à une route dynamique côté next-intl sans exposer ses params, ce qui empêche
            // de recompiler l'URL localisée. On bascule alors la locale sur le chemin réel
            // et on laisse le middleware next-intl rediriger vers la bonne version.
            const segments = rawPathname.split("/");
            if (routing.locales.includes(segments[1])) {
                segments[1] = language;
            } else {
                segments.splice(1, 0, language);
            }
            window.location.assign(segments.join("/") || `/${language}`);
        }
    };

    const availableLanguages = labels;

    return (
        <div className={`${compact ? "w-[58px]" : "md:w-[230px] w-[290px]"} h-full bg-[#F4F5F7] dark:bg-bgDark-500 border-r border-gray-200 dark:border-borderDark flex flex-col`}>
            <div className={`flex-1 min-h-0 ${compact ? "" : "overflow-y-auto"}`}>
                {/* Logo + Collapse toggle */}
                {compact ? (
                    <div className="pt-3 flex flex-col items-center gap-3">
                        <Link href="/" className="inline-block" aria-label="Believemy">
                            <div className="dark:hidden">
                                <Logo icon />
                            </div>
                            <div className="hidden dark:block">
                                <Logo icon white />
                            </div>
                        </Link>
                        {onExpand && (
                            <button
                                type="button"
                                onClick={onExpand}
                                aria-label={t("SideHeader_expand")}
                                title={t("SideHeader_expand")}
                                className="hidden lg:flex items-center justify-center w-8 h-8 rounded-md text-gray-500 dark:text-grayDark-400 hover:bg-gray-200 dark:hover:bg-bgDark-300 hover:text-gray-900 dark:hover:text-white duration-150"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M9 6l6 6-6 6" />
                                </svg>
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="px-5 pt-5 pb-4 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1 min-w-0">
                            {/* For light theme */}
                            <Link href="/" className="inline-block">
                                <div className="dark:hidden">
                                    <Logo ratio="0.9" />
                                </div>
                                {/* For dark theme */}
                                <div className="hidden dark:block">
                                    <Logo ratio="0.9" white />
                                </div>
                            </Link>
                            <PinkOctober />
                        </div>
                        {onCollapse && (
                            <button
                                type="button"
                                onClick={onCollapse}
                                aria-label={t("SideHeader_collapse")}
                                title={t("SideHeader_collapse")}
                                className="hidden lg:flex items-center justify-center w-7 h-7 rounded-md text-gray-500 dark:text-grayDark-400 hover:bg-gray-200 dark:hover:bg-bgDark-300 hover:text-gray-900 dark:hover:text-white duration-150"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M15 6l-6 6 6 6" />
                                </svg>
                            </button>
                        )}
                    </div>
                )}

                {/* Nav */}
                <nav className={compact ? "p-2 pt-3" : "p-3"}>
                    <ul className="flex flex-col gap-0.5">
                        {/* Main */}
                        <li>
                            <NewLinkNave
                                href="/"
                                page={page}
                                name="dashboard"
                                compact={compact}
                                label={t("SideHeader_dashboard")}
                                currentSVG={
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12s4.477 10 10 10m1.956-7.905c-.193.17-.44.268-.932.465c-2.31.924-3.465 1.386-4.124.938a1.5 1.5 0 0 1-.398-.398c-.448-.66.014-1.814.938-4.124c.197-.493.295-.74.465-.933c.043-.049.09-.095.139-.138c.193-.17.44-.268.932-.465c2.31-.924 3.464-1.386 4.124-.938a1.5 1.5 0 0 1 .398.398c.448.66-.014 1.814-.938 4.124c-.197.493-.295.739-.465.932c-.043.05-.09.096-.139.139"
                                        clipRule="evenodd"
                                    ></path>
                                }
                                notCurrentSVG={
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="M12 2.75a9.25 9.25 0 1 0 0 18.5a9.25 9.25 0 0 0 0-18.5M1.25 12C1.25 6.063 6.063 1.25 12 1.25S22.75 6.063 22.75 12S17.937 22.75 12 22.75S1.25 17.937 1.25 12m12.599-2.819c-.605.16-1.42.485-2.595.955c-.541.217-.642.268-.716.333a.758.758 0 0 0-.07.069c-.064.074-.115.175-.332.716c-.47 1.175-.794 1.99-.955 2.595c-.167.63-.085.79-.059.83a.75.75 0 0 0 .2.198c.038.027.199.108.829-.059c.605-.16 1.42-.484 2.594-.954c.542-.217.643-.268.717-.333l.494.564l-.494-.564a.733.733 0 0 0 .07-.07l.563.495l-.564-.494c.065-.074.116-.175.333-.717c.47-1.174.794-1.99.954-2.594c.168-.63.086-.791.06-.83a.75.75 0 0 0-.2-.199c-.038-.026-.2-.108-.83.06m-.384-1.45c.69-.183 1.436-.271 2.057.15c.234.16.437.362.596.597c.422.621.333 1.367.15 2.057c-.186.704-.546 1.605-.991 2.717l-.02.05l-.034.084c-.163.41-.308.772-.564 1.065a2.248 2.248 0 0 1-.208.208c-.293.256-.655.4-1.065.564l-.083.033l-.279-.696l.279.697l-.05.02c-1.113.445-2.014.805-2.718.991c-.69.183-1.436.272-2.057-.15l.422-.62l-.422.62a2.25 2.25 0 0 1-.596-.596c-.422-.621-.334-1.368-.15-2.057c.186-.705.546-1.605.991-2.717l.02-.05l.034-.085c.163-.41.307-.771.564-1.064c.065-.074.134-.143.208-.208c.293-.257.655-.401 1.064-.564l.084-.034l.05-.02c1.113-.445 2.013-.805 2.718-.992"
                                        clipRule="evenodd"
                                    ></path>
                                }
                                sideNavIsOpen={sideNavIsOpen}
                            >
                                <div>{t("SideHeader_dashboard")}</div>
                            </NewLinkNave>
                        </li>

                        {/* Reading List - connected users only */}
                        {session && session.user && (
                            <li>
                                <NewLinkNave
                                    href="/reading-list"
                                    page={page}
                                    name="reading-list"
                                    compact={compact}
                                    label={t("SideHeader_readingList")}
                                    currentSVG={
                                        <path
                                            fill="currentColor"
                                            d="M6.477 2.25h11.046c.91 0 1.674 0 2.286.082c.655.088 1.261.284 1.75.773c.49.49.685 1.095.773 1.75c.082.612.082 1.376.082 2.287V21a.75.75 0 0 1-1.135.643l-2.792-1.674c-.55-.33-.91-.545-1.213-.668c-.288-.117-.469-.143-.65-.143s-.362.026-.65.143c-.302.123-.663.337-1.213.668l-1.94 1.165a2.96 2.96 0 0 1-3.044 0l-1.94-1.165c-.55-.33-.911-.545-1.213-.668c-.288-.117-.469-.143-.65-.143s-.362.026-.65.143c-.302.123-.663.337-1.213.668L2.385 21.64A.75.75 0 0 1 1.25 21V7.142c0-.91 0-1.675.082-2.287c.088-.655.284-1.26.773-1.75c.49-.489 1.095-.685 1.75-.773c.612-.082 1.376-.082 2.287-.082Z"
                                        ></path>
                                    }
                                    notCurrentSVG={
                                        <g
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M4 17V7c0-2.357 0-3.536.732-4.268C5.464 2 6.643 2 9 2h6c2.357 0 3.536 0 4.268.732C20 3.464 20 4.643 20 7v14l-2.793-1.676c-1.097-.658-1.645-.987-2.262-1.05a3 3 0 0 0-.59 0c-.617.063-1.165.392-2.261 1.05a2.21 2.21 0 0 1-2.275 0c-1.097-.658-1.645-.987-2.262-1.05a3 3 0 0 0-.59 0c-.617.063-1.165.392-2.261 1.05L4 21Z"></path>
                                        </g>
                                    }
                                    sideNavIsOpen={sideNavIsOpen}
                                >
                                    <div>{t("SideHeader_readingList")}</div>
                                </NewLinkNave>
                            </li>
                        )}

                        {/* Leagues - connected users only */}
                        {session && session.user && (
                            <li>
                                <NewLinkNave
                                    href="/leagues"
                                    page={page}
                                    name="leagues"
                                    compact={compact}
                                    label={t("SideHeader_leagues")}
                                    currentSVG={
                                        <path
                                            fill="currentColor"
                                            d="M5 22h14v-2H5zM17.95 4H20a1 1 0 0 1 1 1v3a3 3 0 0 1-3 3h-1.3a5 5 0 0 1-3.7 2.9V16a2 2 0 0 1 2 2v1H9v-1a2 2 0 0 1 2-2v-2.1A5 5 0 0 1 7.3 11H6a3 3 0 0 1-3-3V5a1 1 0 0 1 1-1h2.05A5 5 0 0 1 6 3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1a5 5 0 0 1-.05 1M5 6v2a1 1 0 0 0 1 1V6zm14 0h-1v3a1 1 0 0 0 1-1z"
                                        ></path>
                                    }
                                    notCurrentSVG={
                                        <g
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M7 4h10v4a5 5 0 0 1-10 0z"></path>
                                            <path
                                                strokeLinecap="round"
                                                d="M7 5H4v3a3 3 0 0 0 3 3m10-6h3v3a3 3 0 0 1-3 3m-2 2v3a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-3m-2 8h10"
                                            ></path>
                                        </g>
                                    }
                                    sideNavIsOpen={sideNavIsOpen}
                                >
                                    <div>{t("SideHeader_leagues")}</div>
                                </NewLinkNave>
                            </li>
                        )}

                        {/* SECTION */}
                        {compact ? (
                            <li className="my-2 mx-2 h-px bg-[#05060F]/5 dark:bg-bgDark-550/40" aria-hidden="true" />
                        ) : (
                            <li className="after-line dark:[&>div]:!bg-bgDark-500 dark:[&::after]:!bg-bgDark-550/40">
                                <div>{t("SideHeader_doneYourself")}</div>
                            </li>
                        )}

                        {/* Done Yourself */}
                        <li>
                            <NewLinkNave
                                href="/courses"
                                page={page}
                                name="courses"
                                compact={compact}
                                label={t("SideHeader_courses")}
                                currentSVG={
                                    <>
                                        <path
                                            fill="currentColor"
                                            d="M14.217 3.5a5.17 5.17 0 0 0-4.434 0L3.092 6.637c-1.076.504-1.357 1.927-.842 2.91V14.5a.75.75 0 0 0 1.5 0v-3.828L9.783 13.5a5.17 5.17 0 0 0 4.434 0l6.691-3.137c1.456-.682 1.456-3.044 0-3.726z"
                                        ></path>
                                        <path
                                            fill="currentColor"
                                            d="M5 12.915v3.71c0 1.008.503 1.952 1.385 2.44C7.854 19.88 10.204 21 12 21c1.796 0 4.146-1.121 5.615-1.934c.882-.489 1.385-1.433 1.385-2.44v-3.711l-4.146 1.944a6.67 6.67 0 0 1-5.708 0z"
                                        ></path>
                                    </>
                                }
                                notCurrentSVG={
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="M9.464 2.82a5.92 5.92 0 0 1 5.072 0l6.69 3.138C22.28 6.45 22.75 7.522 22.75 8.5c0 .978-.47 2.05-1.523 2.543l-1.477.692v4.89c0 1.253-.628 2.463-1.772 3.097c-.752.416-1.74.917-2.78 1.316c-1.027.394-2.164.712-3.198.712c-1.034 0-2.171-.318-3.199-.712c-1.04-.399-2.027-.9-2.779-1.316c-1.144-.634-1.772-1.844-1.772-3.097v-4.89l-1.477-.693l-.023-.01V14a.75.75 0 0 1-1.5 0V8.5c0-.978.471-2.05 1.523-2.542zM5.75 12.439v4.187c0 .763.379 1.441.999 1.785a21.14 21.14 0 0 0 2.59 1.227c.963.37 1.899.613 2.661.613s1.698-.243 2.662-.613a21.14 21.14 0 0 0 2.59-1.227c.62-.344.998-1.022.998-1.785v-4.187l-3.714 1.741a5.92 5.92 0 0 1-5.072 0zM3.41 9.684c-.403-.189-.66-.64-.66-1.184c0-.544.257-.995.66-1.184l6.691-3.137a4.42 4.42 0 0 1 3.798 0l6.691 3.137c.403.189.66.64.66 1.184c0 .544-.257.995-.66 1.184l-6.691 3.137a4.42 4.42 0 0 1-3.798 0z"
                                        clipRule="evenodd"
                                    ></path>
                                }
                                sideNavIsOpen={sideNavIsOpen}
                            >
                                <div>{t("SideHeader_courses")}</div>
                            </NewLinkNave>
                        </li>

                        {/* Reps : seulement pour les comptes de la liste figée
                            (User::repsOnWeb). Pour les autres, Reps vit dans
                            l'application ; la page /reps reste ouverte. */}
                        {session?.user?.repsOnWeb && (
                        <li>
                            <NewLinkNave
                                href="/reps"
                                page={page}
                                name="reps"
                                compact={compact}
                                label={t("SideHeader_reps")}
                                currentSVG={
                                    <path
                                        fill="currentColor"
                                        d="M22 14v-4c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C20.398 7 19.932 7 19 7s-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C16 8.602 16 9.568 16 10.5H8c0-.932 0-1.898-.152-2.265a2 2 0 0 0-1.083-1.083C6.398 7 5.932 7 5 7s-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C2 8.602 2 9.068 2 10v4c0 .932 0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C3.602 17 4.068 17 5 17s1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C8 15.398 8 14.432 8 13.5h8c0 .932 0 1.898.152 2.265a2 2 0 0 0 1.083 1.083C17.602 17 18.068 17 19 17s1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C22 15.398 22 14.932 22 14"
                                    ></path>
                                }
                                notCurrentSVG={
                                    <path
                                        fill="currentColor"
                                        d="m17.235 16.848l-.287.693zM16 13.5h.75a.75.75 0 0 0-.75-.75zm.152 2.265l-.693.287zm5.696 0l.693.287zm-1.083 1.083l-.287-.693zm0-9.696l-.287.693zm1.083 1.083l.693-.287zm-4.613-1.083l-.287-.693zM16 10.5v.75a.75.75 0 0 0 .75-.75zm.152-2.265l-.693-.287zM3.235 16.848l-.287.693zm-1.083-1.083l-.693.287zM8 13.5v-.75a.75.75 0 0 0-.75.75zm-.152 2.265l.693.287zm-1.083 1.083l.287.693zm0-9.696l.287-.693zM8 10.5h-.75c0 .414.336.75.75.75zm-.152-2.265l.693-.287zM3.235 7.152l-.287-.693zM2.152 8.235l-.693-.287zM21.25 10v4h1.5v-4zM19 16.25c-.476 0-.796 0-1.043-.017c-.241-.017-.358-.046-.435-.078l-.574 1.386c.29.12.59.167.907.188c.31.021.69.021 1.145.021zm-3.75-2.75c0 .461 0 .954.02 1.38c.01.213.026.421.051.609c.024.176.063.382.138.563l1.386-.574l-.012-.045a2 2 0 0 1-.026-.147a6 6 0 0 1-.039-.477a30 30 0 0 1-.018-1.309zm2.272 2.655a1.25 1.25 0 0 1-.677-.677l-1.386.574a2.75 2.75 0 0 0 1.489 1.489zM21.25 14c0 .476 0 .796-.017 1.043c-.017.241-.046.358-.078.435l1.386.574c.12-.29.167-.59.188-.907c.021-.31.021-.69.021-1.145zM19 17.75c.456 0 .835 0 1.145-.02c.317-.022.617-.069.907-.19l-.574-1.385c-.077.032-.194.061-.435.078c-.247.017-.567.017-1.043.017zm2.155-2.272a1.25 1.25 0 0 1-.677.677l.574 1.386a2.75 2.75 0 0 0 1.489-1.489zM19 7.75c.476 0 .796 0 1.043.017c.241.017.358.046.435.078l.574-1.386c-.29-.12-.59-.167-.907-.188c-.31-.021-.69-.021-1.145-.021zM22.75 10c0-.456 0-.835-.02-1.145a2.8 2.8 0 0 0-.19-.907l-1.385.574c.032.077.061.194.078.435c.017.247.017.567.017 1.043zm-2.272-2.155c.307.127.55.37.677.677l1.386-.574a2.75 2.75 0 0 0-1.489-1.489zM19 6.25c-.456 0-.835 0-1.145.02a2.8 2.8 0 0 0-.907.19l.574 1.385c.077-.032.194-.061.435-.078c.247-.017.567-.017 1.043-.017zm-2.25 4.25c0-.47 0-.926.018-1.309q.014-.286.04-.477q.012-.093.025-.147l.012-.045l-1.386-.574a2.3 2.3 0 0 0-.138.563a7 7 0 0 0-.051.61c-.02.425-.02.918-.02 1.379zm.198-4.04a2.75 2.75 0 0 0-1.489 1.488l1.386.574c.127-.307.37-.55.677-.677zM2.75 14v-4h-1.5v4zM5 16.25c-.476 0-.796 0-1.043-.017c-.241-.017-.358-.046-.435-.078l-.574 1.386c.29.12.59.167.907.188c.31.021.69.021 1.145.021zM1.25 14c0 .456 0 .835.02 1.145c.022.318.07.617.19.907l1.385-.574c-.032-.077-.061-.194-.078-.435A17 17 0 0 1 2.75 14zm2.272 2.155a1.25 1.25 0 0 1-.677-.677l-1.386.574a2.75 2.75 0 0 0 1.489 1.489zM7.25 13.5c0 .47 0 .926-.018 1.309q-.014.286-.04.477a2 2 0 0 1-.025.147l-.012.045l1.386.574c.075-.181.114-.387.138-.563c.025-.188.041-.396.051-.61c.02-.425.02-.918.02-1.379zM5 17.75c.456 0 .835 0 1.145-.02c.317-.022.617-.069.907-.19l-.574-1.385c-.077.032-.194.061-.435.078A17 17 0 0 1 5 16.25zm2.155-2.272a1.25 1.25 0 0 1-.677.677l.574 1.386a2.75 2.75 0 0 0 1.489-1.489zM5 7.75c.476 0 .796 0 1.043.017c.241.017.358.046.435.078l.574-1.386c-.29-.12-.59-.167-.907-.188c-.31-.021-.69-.021-1.145-.021zm3.75 2.75c0-.461 0-.954-.02-1.38a7 7 0 0 0-.051-.609a2.3 2.3 0 0 0-.138-.563l-1.386.574l.012.045q.012.053.026.147a6 6 0 0 1 .039.477c.018.383.018.838.018 1.309zM6.478 7.845c.307.127.55.37.677.677l1.386-.574a2.75 2.75 0 0 0-1.489-1.489zM5 6.25c-.456 0-.835 0-1.145.02a2.8 2.8 0 0 0-.907.19l.574 1.385c.077-.032.194-.061.435-.078A17 17 0 0 1 5 7.75zM2.75 10c0-.476 0-.796.017-1.043c.017-.241.046-.358.078-.435L1.46 7.948c-.12.29-.167.59-.188.907c-.021.31-.021.69-.021 1.145zm.198-3.54a2.75 2.75 0 0 0-1.489 1.488l1.386.574c.127-.307.37-.55.677-.677zM8 11.25h8v-1.5H8zm8 1.5H8v1.5h8z"
                                    ></path>
                                }
                                sideNavIsOpen={sideNavIsOpen}
                            >
                                <div>{t("SideHeader_reps")}</div>
                            </NewLinkNave>
                        </li>
                        )}

                        <li>
                            <NewLinkNave
                                href="/articles"
                                page={page}
                                name="articles"
                                compact={compact}
                                label={t("SideHeader_articles")}
                                currentSVG={
                                    <>
                                        <path
                                            fill="currentColor"
                                            fillRule="evenodd"
                                            d="M14.25 4.48v3.057c0 .111 0 .27.021.406a.937.937 0 0 0 .444.683a.96.96 0 0 0 .783.072c.13-.04.272-.108.378-.159L17 8.005l1.124.534c.106.05.248.119.378.16a.958.958 0 0 0 .783-.073a.937.937 0 0 0 .444-.683c.022-.136.021-.295.021-.406V3.031c.113-.005.224-.01.332-.013C21.154 2.98 22 3.86 22 4.933v11.21c0 1.112-.906 2.01-2.015 2.08c-.97.06-2.108.179-2.985.41c-1.082.286-2.373.904-3.372 1.436c-.28.149-.575.257-.878.323V5.174a3.57 3.57 0 0 0 .924-.371c.184-.107.377-.216.576-.323m5.478 8.338a.75.75 0 0 1-.546.91l-4 1a.75.75 0 1 1-.364-1.456l4-1a.75.75 0 0 1 .91.546M11.25 5.214a3.444 3.444 0 0 1-.968-.339C9.296 4.354 8.05 3.765 7 3.487c-.887-.233-2.041-.352-3.018-.412C2.886 3.008 2 3.9 2 4.998v11.146c0 1.11.906 2.01 2.015 2.079c.97.06 2.108.179 2.985.41c1.081.286 2.373.904 3.372 1.436c.28.149.575.257.878.324zM4.273 8.818a.75.75 0 0 1 .91-.546l4 1a.75.75 0 1 1-.365 1.456l-4-1a.75.75 0 0 1-.545-.91m.91 3.454a.75.75 0 1 0-.365 1.456l4 1a.75.75 0 0 0 .364-1.456z"
                                            clipRule="evenodd"
                                        ></path>
                                        <path
                                            fill="currentColor"
                                            d="M18.25 3.151c-.62.073-1.23.18-1.75.336a8.2 8.2 0 0 0-.75.27v3.182l.75-.356l.008-.005a1.13 1.13 0 0 1 .492-.13c.048 0 .094.004.138.01c.175.029.315.1.354.12l.009.005l.75.356V3.647z"
                                        ></path>
                                    </>
                                }
                                notCurrentSVG={
                                    <>
                                        <path
                                            fill="currentColor"
                                            d="M19.728 12.818a.75.75 0 0 0-.91-.546l-4 1a.75.75 0 0 0 .364 1.456l4-1a.75.75 0 0 0 .546-.91M19.75 5.5a.75.75 0 0 0-1.5 0v3.439l-.75-.356l-.008-.005a1.13 1.13 0 0 0-.492-.13a1.13 1.13 0 0 0-.492.13l-.009.005l-.749.356V6.95a.75.75 0 0 0-1.5 0v2.587c0 .111 0 .27.02.406a.936.936 0 0 0 .445.683a.96.96 0 0 0 .783.072c.13-.04.272-.108.378-.159L17 10.005l1.124.534c.106.05.248.119.378.16a.957.957 0 0 0 .783-.073a.936.936 0 0 0 .444-.683c.021-.136.021-.295.021-.406zM4.272 8.818a.75.75 0 0 1 .91-.546l4 1a.75.75 0 1 1-.364 1.456l-4-1a.75.75 0 0 1-.546-.91m.91 3.454a.75.75 0 0 0-.364 1.456l4 1a.75.75 0 1 0 .364-1.456z"
                                        ></path>
                                        <path
                                            fill="currentColor"
                                            fillRule="evenodd"
                                            d="M22.75 4.933c0-1.463-1.165-2.718-2.694-2.665c-1.139.04-2.626.158-3.771.501c-.995.298-2.114.88-2.987 1.385a2.77 2.77 0 0 1-2.665.058c-.996-.526-2.305-1.15-3.442-1.45c-.96-.253-2.176-.375-3.163-.436C2.47 2.231 1.25 3.5 1.25 4.998v11.146c0 1.536 1.244 2.735 2.718 2.827c.959.06 2.038.176 2.84.388c.993.261 2.22.844 3.212 1.372a4.224 4.224 0 0 0 3.96 0c.991-.528 2.219-1.11 3.211-1.372c.803-.212 1.882-.328 2.841-.388c1.474-.092 2.718-1.291 2.718-2.827zm-2.642-1.166c.615-.021 1.142.485 1.142 1.166v11.21c0 .686-.568 1.285-1.312 1.331c-.98.062-2.179.183-3.13.434c-1.17.31-2.525.962-3.533 1.5c-.168.09-.344.16-.525.211V5.942c.452-.09.892-.254 1.3-.49c.85-.492 1.845-1 2.665-1.246c.95-.284 2.274-.4 3.393-.439M11.25 5.975a4.199 4.199 0 0 1-1.318-.436c-.977-.517-2.161-1.072-3.123-1.326c-.813-.215-1.907-.33-2.873-.39c-.634-.038-1.186.478-1.186 1.175v11.146c0 .685.568 1.284 1.312 1.33c.98.062 2.179.183 3.13.434c1.17.31 2.525.962 3.533 1.5c.168.09.345.16.525.211z"
                                            clipRule="evenodd"
                                        ></path>
                                    </>
                                }
                                sideNavIsOpen={sideNavIsOpen}
                            >
                                <div>{t("SideHeader_articles")}</div>
                            </NewLinkNave>
                        </li>

                        {/* Glossaries - moved here from OUTILS */}
                        <li>
                            <NewLinkNave
                                href="/glossaries"
                                page={page}
                                name="glossaries"
                                compact={compact}
                                label={t("SideHeader_glossaries")}
                                currentSVG={
                                    <>
                                        <path
                                            fill="currentColor"
                                            fillRule="evenodd"
                                            d="M6.271 2.112c-.81.106-1.238.301-1.544.6c-.305.3-.504.72-.613 1.513C4.002 5.042 4 6.124 4 7.675v8.57a4.172 4.172 0 0 1 1.299-.593c.528-.139 1.144-.139 2.047-.138H20V7.676c0-1.552-.002-2.634-.114-3.451c-.109-.793-.308-1.213-.613-1.513c-.306-.299-.734-.494-1.544-.6c-.834-.11-1.938-.112-3.522-.112H9.793c-1.584 0-2.688.002-3.522.112m.488 4.483c0-.448.37-.811.827-.811h8.828a.82.82 0 0 1 .827.81a.82.82 0 0 1-.827.811H7.586a.82.82 0 0 1-.827-.81m.827 2.973a.82.82 0 0 0-.827.81c0 .448.37.811.827.811h5.517a.82.82 0 0 0 .828-.81a.82.82 0 0 0-.828-.811z"
                                            clipRule="evenodd"
                                        ></path>
                                        <path
                                            fill="currentColor"
                                            d="M8.69 17.135H7.473c-1.079 0-1.456.007-1.746.083a2.464 2.464 0 0 0-1.697 1.538c.016.382.043.719.084 1.019c.109.793.308 1.213.613 1.513c.306.299.734.494 1.544.6c.834.11 1.938.112 3.522.112h4.414c1.584 0 2.688-.002 3.522-.111c.81-.107 1.238-.302 1.544-.601c.305-.3.504-.72.613-1.513c.092-.666.11-1.51.113-2.64h-6.896v3.007c0 .298 0 .447-.104.507c-.105.06-.248-.007-.534-.14l-1.371-.638c-.097-.045-.145-.067-.197-.067c-.053 0-.101.022-.198.067l-1.37.638c-.287.133-.43.2-.535.14c-.104-.06-.104-.21-.104-.507z"
                                        ></path>
                                    </>
                                }
                                notCurrentSVG={
                                    <>
                                        <g
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M4 8c0-2.828 0-4.243.879-5.121C5.757 2 7.172 2 10 2h4c2.828 0 4.243 0 5.121.879C20 3.757 20 5.172 20 8v8c0 2.828 0 4.243-.879 5.121C18.243 22 16.828 22 14 22h-4c-2.828 0-4.243 0-5.121-.879C4 20.243 4 18.828 4 16z"></path>
                                            <path d="M19.898 16h-12c-.93 0-1.395 0-1.777.102A3 3 0 0 0 4 18.224"></path>
                                            <path
                                                strokeLinecap="round"
                                                d="M8 7h8m-8 3.5h5m0 5.5v3.53c0 .276 0 .414-.095.47c-.095.056-.224-.006-.484-.13l-1.242-.59c-.088-.04-.132-.062-.179-.062c-.047 0-.091.021-.179.063l-1.242.59c-.26.123-.39.185-.484.129C9 19.944 9 19.806 9 19.53v-3.08"
                                            />
                                        </g>
                                    </>
                                }
                                sideNavIsOpen={sideNavIsOpen}
                            >
                                {t("SideHeader_glossaries")}
                            </NewLinkNave>
                        </li>

                        {/* Outils : calculateurs et assistants, à la suite du
                            glossaire dont ils prolongent les fiches. */}
                        <li>
                            <NewLinkNave
                                href="/tools"
                                page={page}
                                name="tools"
                                compact={compact}
                                label={t("SideHeader_toolbox")}
                                currentSVG={
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="M14.4 2.4a5.5 5.5 0 0 0-5.06 7.67L2.9 16.5a2.6 2.6 0 0 0 3.68 3.68l6.43-6.43a5.5 5.5 0 0 0 6.92-7.03a.75.75 0 0 0-1.22-.26l-2.3 2.3a1 1 0 0 1-1.42 0l-.75-.75a1 1 0 0 1 0-1.41l2.3-2.3a.75.75 0 0 0-.26-1.23a5.5 5.5 0 0 0-1.88-.33M5.5 18.5a1 1 0 1 1 0-2a1 1 0 0 1 0 2"
                                        clipRule="evenodd"
                                    />
                                }
                                notCurrentSVG={
                                    <g
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M14.4 3.15a4.75 4.75 0 0 0-4.2 6.93L3.43 16.85a1.85 1.85 0 1 0 2.62 2.62l6.77-6.77a4.75 4.75 0 0 0 6.02-6.13l-2.3 2.3a1.75 1.75 0 0 1-2.47 0l-.75-.75a1.75 1.75 0 0 1 0-2.48l2.3-2.3a4.7 4.7 0 0 0-1.22-.19Z" />
                                        <path d="M5.5 17.5h.01" />
                                    </g>
                                }
                                sideNavIsOpen={sideNavIsOpen}
                            >
                                {t("SideHeader_toolbox")}
                            </NewLinkNave>
                        </li>

                        {!session && !sessionLoading && (
                            <>
                                <li>
                                    <NewLinkNave
                                        href="/pricing"
                                        page={page}
                                        name="pricing"
                                        compact={compact}
                                        label={t("SideHeader_pricing")}
                                        currentSVG={
                                            <>
                                                <path
                                                    fill="currentColor"
                                                    d="M9.034 5.963L6.491 8.5c-.467.466-.896.893-1.235 1.28a6 6 0 0 0-.619.82l-.024-.025l-.095-.094a4.9 4.9 0 0 0-1.532-1.004l-.123-.05l-.379-.15a.764.764 0 0 1-.259-1.252C3.345 6.907 4.69 5.566 5.34 5.297a3.4 3.4 0 0 1 1.788-.229c.546.081 1.063.362 1.907.895m4.342 13.35c.205.208.34.355.464.512q.243.311.434.658c.142.26.253.537.474 1.092a.69.69 0 0 0 1.126.224l.084-.083c1.12-1.117 2.465-2.458 2.735-3.105a3.35 3.35 0 0 0 .229-1.782c-.081-.545-.362-1.06-.897-1.902l-2.552 2.544c-.478.477-.916.914-1.313 1.256c-.237.206-.497.41-.784.586"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    fillRule="evenodd"
                                                    d="m14.447 16.377l5.847-5.83c.842-.839 1.263-1.259 1.484-1.792S22 7.627 22 6.44v-.567c0-1.826 0-2.739-.569-3.306S19.947 2 18.116 2h-.57c-1.19 0-1.785 0-2.32.221c-.536.221-.957.641-1.8 1.48L7.58 9.531c-.984.98-1.594 1.589-1.83 2.176a1.5 1.5 0 0 0-.112.562c0 .802.647 1.448 1.942 2.739l.174.173l2.038-2.069a.75.75 0 1 1 1.069 1.053L8.816 16.24l.137.137c1.295 1.29 1.943 1.936 2.747 1.936c.178 0 .348-.031.519-.094c.603-.222 1.219-.836 2.228-1.842m2.747-6.846a1.946 1.946 0 0 1-2.747 0a1.93 1.93 0 0 1 0-2.738a1.946 1.946 0 0 1 2.747 0a1.93 1.93 0 0 1 0 2.738"
                                                    clipRule="evenodd"
                                                />
                                            </>
                                        }
                                        notCurrentSVG={
                                            <>
                                                <path
                                                    fill="currentColor"
                                                    fillRule="evenodd"
                                                    d="M17.406 1.25h.761c.872 0 1.6 0 2.18.078c.61.082 1.166.262 1.613.708s.63 1.001.712 1.612c.078.578.078 1.304.078 2.173v.76c0 1.05 0 1.787-.28 2.461s-.802 1.195-1.547 1.937l-.1.1l-2.67 2.662l.093.143c.394.608.713 1.1.943 1.525c.238.44.408.853.476 1.31a4.1 4.1 0 0 1-.28 2.18c-.107.258-.292.527-.482.772c-.2.258-.444.538-.706.823a58 58 0 0 1-1.702 1.746l-.091.09c-.72.718-1.968.486-2.352-.477c-.227-.572-.32-.8-.435-1.01a4 4 0 0 0-.367-.555c-.146-.187-.316-.36-.74-.781l-.505-.443l-.02-.019a2 2 0 0 1-.285.018c-.632 0-1.153-.263-1.644-.637c-.464-.353-.98-.867-1.596-1.481l-1.447-1.443c-.616-.614-1.131-1.128-1.486-1.591c-.374-.49-.64-1.01-.64-1.642q.001-.17.026-.337l-.83-.827l-.087-.087a4.1 4.1 0 0 0-1.41-.895l-.378-.15a1.514 1.514 0 0 1-.513-2.48l.007-.008a59 59 0 0 1 1.751-1.697c.287-.261.568-.504.825-.703c.247-.19.517-.375.774-.481a4.13 4.13 0 0 1 2.184-.278c.457.067.87.236 1.311.474c.426.23.92.547 1.53.94l.148.095l2.672-2.665l.1-.099c.744-.743 1.266-1.264 1.942-1.543s1.413-.278 2.467-.278M9.137 6.919c-.56-.36-.963-.616-1.301-.799c-.36-.193-.6-.278-.82-.31a2.63 2.63 0 0 0-1.39.179a2.4 2.4 0 0 0-.432.284c-.212.163-.459.376-.73.623a57 57 0 0 0-1.71 1.658l-.004.006v.007l.003.008l.008.004l.383.152l.128.051a5.6 5.6 0 0 1 1.768 1.16c.028.026.055.053.098.097l.478.476q.098-.122.204-.242c.322-.368.735-.78 1.214-1.257zm4.317 11.42l.081.071l.052.052c.39.39.633.63.844.9q.281.36.501.76c.16.291.284.598.477 1.083l.02-.02a57 57 0 0 0 1.663-1.705c.248-.27.462-.517.625-.728a2.3 2.3 0 0 0 .285-.43a2.6 2.6 0 0 0 .179-1.383c-.032-.218-.117-.457-.31-.815c-.184-.338-.441-.74-.804-1.3l-2.109 2.102c-.49.488-.912.909-1.287 1.233a8 8 0 0 1-.217.18M17.547 2.75c-1.252 0-1.67.013-2.036.164c-.365.151-.67.436-1.555 1.319l-5.847 5.829c-.498.497-.876.874-1.16 1.2c-.287.325-.433.549-.504.725a.7.7 0 0 0-.058.282c0 .17.059.374.332.73c.285.374.728.817 1.39 1.478l.157.156l1.53-1.526a.75.75 0 0 1 1.06 1.063l-1.527 1.522l.153.154c.663.66 1.107 1.101 1.482 1.386c.358.272.563.331.736.331a.7.7 0 0 0 .26-.048c.175-.064.398-.204.73-.49c.33-.286.716-.67 1.227-1.18l5.847-5.828c.885-.883 1.17-1.186 1.321-1.55s.165-.778.165-2.027v-.567c0-.934-.002-1.56-.065-2.025c-.06-.443-.162-.629-.284-.75s-.308-.224-.754-.284c-.468-.062-1.095-.064-2.032-.064zm-.884 4.574a1.196 1.196 0 0 0-1.687 0a1.18 1.18 0 0 0 0 1.676a1.196 1.196 0 0 0 1.687 0a1.18 1.18 0 0 0 0-1.676M13.917 6.26a2.696 2.696 0 0 1 3.806 0a2.68 2.68 0 0 1 0 3.801a2.696 2.696 0 0 1-3.806 0a2.68 2.68 0 0 1 0-3.8"
                                                    clipRule="evenodd"
                                                />
                                            </>
                                        }
                                        sideNavIsOpen={sideNavIsOpen}
                                    >
                                        <div>{t("SideHeader_pricing")}</div>
                                    </NewLinkNave>
                                </li>
                            </>
                        )}

                        {compact ? (
                            <li className="my-2 mx-2 h-px bg-[#05060F]/5 dark:bg-bgDark-550/40" aria-hidden="true" />
                        ) : (
                            <li className="after-line dark:[&>div]:!bg-bgDark-500 dark:[&::after]:!bg-bgDark-550/40">
                                <div>{t("SideHeader_doneWithYou")}</div>
                            </li>
                        )}

                        <li>
                            <NewLinkNave
                                href="/accelerators"
                                page={page}
                                name="accelerators"
                                compact={compact}
                                label={t("SideHeader_accelerators")}
                                currentSVG={
                                    <>
                                        <path
                                            fill="currentColor"
                                            d="M12.832 21.801c3.126-.626 7.168-2.875 7.168-8.69c0-5.291-3.873-8.815-6.658-10.434c-.619-.36-1.342.113-1.342.828v1.828c0 1.442-.606 4.074-2.29 5.169c-.86.559-1.79-.278-1.894-1.298l-.086-.838c-.1-.974-1.092-1.565-1.87-.971C4.461 8.46 3 10.33 3 13.11C3 20.221 8.289 22 10.933 22c.154 0 .316-.006.484-.015C10.111 21.874 8 21.064 8 18.444c0-2.05 1.495-3.435 2.631-4.11c.306-.18.663.055.663.41v.59c0 .45.175 1.155.59 1.637c.47.546 1.159-.026 1.214-.744c.018-.226.246-.37.442-.256c.641.375 1.46 1.175 1.46 2.473c0 2.048-1.129 2.99-2.168 3.357"
                                        ></path>
                                    </>
                                }
                                notCurrentSVG={
                                    <>
                                        <g
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M20 13.111C20 20.222 13.956 22 10.933 22C8.29 22 3 20.222 3 13.111c0-2.782 1.461-4.65 2.86-5.716c.778-.594 1.77-.003 1.87.971l.086.838c.105 1.02 1.033 1.857 1.893 1.298C11.394 9.407 12 6.775 12 5.333V5.01c0-1.43 1.444-2.35 2.602-1.512C17.165 5.35 20 8.584 20 13.11Z"></path>
                                            <path d="M8 18.445C8 21.289 10.489 22 11.733 22c1.09 0 3.267-.711 3.267-3.555c0-1.102-.59-1.845-1.16-2.274c-.398-.299-.957-.03-1.094.449c-.178.624-.823 1.016-1.152.456c-.3-.512-.3-1.28-.3-1.743c0-.636-.64-1.048-1.155-.674C9.106 15.409 8 16.68 8 18.445Z"></path>
                                        </g>
                                    </>
                                }
                                sideNavIsOpen={sideNavIsOpen}
                            >
                                <div>{t("SideHeader_accelerators")}</div>
                            </NewLinkNave>
                        </li>

                        {/* Masqué tant que MENTORING_IN_NAVIGATION est à false
                            (next.config.mjs) : le mentorat ne s'atteint alors que
                            par le pied de page et le sitemap. */}
                        {process.env.MENTORING_IN_NAVIGATION && (
                            <li>
                                <NewLinkNave
                                    href="/mentoring"
                                    page={page}
                                    name="mentors"
                                    compact={compact}
                                    label={t("SideHeader_mentors")}
                                    currentSVG={
                                        <>
                                            <path
                                                fill="currentColor"
                                                d="M12 2a5.75 5.75 0 0 0-5.75 5.75v3a5.75 5.75 0 0 0 11.452.75H13a.75.75 0 0 1 0-1.5h4.75V8.5H13A.75.75 0 0 1 13 7h4.701A5.751 5.751 0 0 0 12 2"
                                            ></path>
                                            <path
                                                fill="currentColor"
                                                fillRule="evenodd"
                                                d="M4 9a.75.75 0 0 1 .75.75v1a7.25 7.25 0 1 0 14.5 0v-1a.75.75 0 0 1 1.5 0v1a8.75 8.75 0 0 1-8 8.718v2.282a.75.75 0 0 1-1.5 0v-2.282a8.75 8.75 0 0 1-8-8.718v-1A.75.75 0 0 1 4 9"
                                                clipRule="evenodd"
                                            ></path>
                                        </>
                                    }
                                    notCurrentSVG={
                                        <>
                                            <g fill="none">
                                                <path
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    d="M7 8a5 5 0 0 1 10 0v3a5 5 0 0 1-10 0z"
                                                ></path>
                                                <path
                                                    stroke="currentColor"
                                                    strokeLinecap="round"
                                                    strokeWidth="1.5"
                                                    d="M13 8h4m-4 3h4m-5 8v3"
                                                ></path>
                                                <path
                                                    fill="currentColor"
                                                    d="M20.75 10a.75.75 0 0 0-1.5 0zm-16 0a.75.75 0 0 0-1.5 0zm10.762 7.344a.75.75 0 1 0 .728 1.312zM19.25 10v1h1.5v-1zm-14.5 1v-1h-1.5v1zM12 18.25A7.25 7.25 0 0 1 4.75 11h-1.5A8.75 8.75 0 0 0 12 19.75zM19.25 11a7.248 7.248 0 0 1-3.738 6.344l.728 1.312A8.748 8.748 0 0 0 20.75 11z"
                                                ></path>
                                            </g>
                                        </>
                                    }
                                    sideNavIsOpen={sideNavIsOpen}
                                >
                                    <div>{t("SideHeader_mentors")}</div>
                                </NewLinkNave>
                            </li>
                        )}

                        {/* SECTION COMMUNAUTÉ (reuses SideHeader_tools key) */}
                        {compact ? (
                            <li className="my-2 mx-2 h-px bg-[#05060F]/5 dark:bg-bgDark-550/40" aria-hidden="true" />
                        ) : (
                            <li className="after-line dark:[&>div]:!bg-bgDark-500 dark:[&::after]:!bg-bgDark-550/40">
                                <div>{t("SideHeader_tools")}</div>
                            </li>
                        )}

                        {/* My Profile - connected users only */}
                        {session && session.user && (
                            <li>
                                <NewLinkNave
                                    href={
                                        session.user.pseudo
                                            ? `/@${session.user.pseudo}`
                                            : session.user.userId
                                              ? `/@believer-${session.user.userId}`
                                              : "/account"
                                    }
                                    page={page}
                                    name="profile"
                                    compact={compact}
                                    label={t("SideHeader_myProfile")}
                                    currentSVG={
                                        <>
                                            <path
                                                fill="currentColor"
                                                d="M12 12a5 5 0 1 0 0-10a5 5 0 0 0 0 10"
                                            ></path>
                                            <path
                                                fill="currentColor"
                                                fillRule="evenodd"
                                                d="M12 22c-3.788 0-7.107-1.948-9-4.876c.06-3.347 4.812-5.124 9-5.124s8.94 1.777 9 5.124C19.107 20.052 15.788 22 12 22"
                                                clipRule="evenodd"
                                            ></path>
                                        </>
                                    }
                                    notCurrentSVG={
                                        <>
                                            <g
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="6"
                                                    r="4"
                                                ></circle>
                                                <path d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5Z"></path>
                                            </g>
                                        </>
                                    }
                                    sideNavIsOpen={sideNavIsOpen}
                                >
                                    <div>{t("SideHeader_myProfile")}</div>
                                </NewLinkNave>
                            </li>
                        )}

                        {/* Discord - moved here from SE FORMER */}
                        <li>
                            {compact ? (
                                <div className="relative group">
                                    <Link
                                        href="https://believemy.com/go/sZHSnA"
                                        target="_blank"
                                        className="sidenav-link sidenav-link-compact"
                                        aria-label={t("SideHeader_discord")}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5"
                                            viewBox="0 0 24 24"
                                        >
                                            <g
                                                fill="none"
                                                stroke="currentColor"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="1.5"
                                            >
                                                <path d="M5.5 16c5 2.5 8 2.5 13 0"></path>
                                                <path d="m15.5 17.5l1 2s4.171-1.328 5.5-3.5c0-1 .53-8.147-3-10.5c-1.5-1-4-1.5-4-1.5l-1 2h-2"></path>
                                                <path d="m8.528 17.5l-1 2s-4.171-1.328-5.5-3.5c0-1-.53-8.147 3-10.5c1.5-1 4-1.5 4-1.5l1 2h2"></path>
                                                <path d="M8.5 14c-.828 0-1.5-.895-1.5-2s.672-2 1.5-2s1.5.895 1.5 2s-.672 2-1.5 2m7 0c-.828 0-1.5-.895-1.5-2s.672-2 1.5-2s1.5.895 1.5 2s-.672 2-1.5 2"></path>
                                            </g>
                                        </svg>
                                    </Link>
                                    <span
                                        role="tooltip"
                                        className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md bg-gray-900 dark:bg-grayDark-700 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 shadow-lg"
                                    >
                                        {t("SideHeader_discord")}
                                    </span>
                                </div>
                            ) : (
                                <NewLinkNave
                                    href="https://believemy.com/go/sZHSnA"
                                    page={page}
                                    name="discord"
                                    blank
                                >
                                    <div className="flex justify-between items-center w-full">
                                        <div className="flex items-center gap-3">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-5 h-5"
                                                viewBox="0 0 24 24"
                                            >
                                                <g
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="1.5"
                                                >
                                                    <path d="M5.5 16c5 2.5 8 2.5 13 0"></path>
                                                    <path d="m15.5 17.5l1 2s4.171-1.328 5.5-3.5c0-1 .53-8.147-3-10.5c-1.5-1-4-1.5-4-1.5l-1 2h-2"></path>
                                                    <path d="m8.528 17.5l-1 2s-4.171-1.328-5.5-3.5c0-1-.53-8.147 3-10.5c1.5-1 4-1.5 4-1.5l1 2h2"></path>
                                                    <path d="M8.5 14c-.828 0-1.5-.895-1.5-2s.672-2 1.5-2s1.5.895 1.5 2s-.672 2-1.5 2m7 0c-.828 0-1.5-.895-1.5-2s.672-2 1.5-2s1.5.895 1.5 2s-.672 2-1.5 2"></path>
                                                </g>
                                            </svg>
                                            <div>{t("SideHeader_discord")}</div>
                                        </div>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="1em"
                                            height="1em"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                fill="currentColor"
                                                d="M12 1.25h-.057c-2.309 0-4.118 0-5.53.19c-1.444.194-2.584.6-3.479 1.494c-.895.895-1.3 2.035-1.494 3.48c-.19 1.411-.19 3.22-.19 5.529v.114c0 2.309 0 4.118.19 5.53c.194 1.444.6 2.584 1.494 3.479c.895.895 2.035 1.3 3.48 1.494c1.411.19 3.22.19 5.529.19h.114c2.309 0 4.118 0 5.53-.19c1.444-.194 2.584-.6 3.479-1.494c.895-.895 1.3-2.035 1.494-3.48c.19-1.411.19-3.22.19-5.529V12a.75.75 0 0 0-1.5 0c0 2.378-.002 4.086-.176 5.386c-.172 1.279-.5 2.05-1.069 2.62c-.57.569-1.34.896-2.619 1.068c-1.3.174-3.008.176-5.386.176s-4.086-.002-5.386-.176c-1.279-.172-2.05-.5-2.62-1.069c-.569-.57-.896-1.34-1.068-2.619c-.174-1.3-.176-3.008-.176-5.386s.002-4.086.176-5.386c.172-1.279.5-2.05 1.069-2.62c.57-.569 1.34-.896 2.619-1.068c1.3-.174 3.008-.176 5.386-.176a.75.75 0 0 0 0-1.5"
                                            ></path>
                                            <path
                                                fill="currentColor"
                                                d="M12.47 10.47a.75.75 0 1 0 1.06 1.06l7.72-7.72v3.534a.75.75 0 0 0 1.5 0V2a.75.75 0 0 0-.75-.75h-5.344a.75.75 0 0 0 0 1.5h3.533z"
                                            ></path>
                                        </svg>
                                    </div>
                                </NewLinkNave>
                            )}
                        </li>
                    </ul>
                </nav>
            </div>
            {/* Assistance */}
            {compact ? (
                <div className="flex flex-col items-center gap-3 px-2 pb-4 pt-4 border-t border-gray-200 dark:border-borderDark">
                    {session && !sessionLoading && session.user && session.user.currentTeam && (
                        <div className="relative group">
                            <TeamSwitcher
                                currentTeam={session.user.currentTeam}
                                availableTeams={session.user.availableTeams || []}
                                showCreateTeam={false}
                                useModal
                                iconOnly
                            />
                            <span
                                role="tooltip"
                                className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md bg-gray-900 dark:bg-grayDark-700 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 shadow-lg"
                            >
                                {session.user.currentTeam.name}
                            </span>
                        </div>
                    )}
                    {session && !sessionLoading && session.user && <FeatureSuggestion compact />}
                    {session && !sessionLoading && session.user && (() => {
                        const { level, progressToNextLevel } = calculatePlayerLevel(session.user.xp || 0);
                        const radius = 16;
                        const circumference = 2 * Math.PI * radius;
                        const offset = circumference - (progressToNextLevel / 100) * circumference;
                        return (
                            <div className="relative group">
                                <div
                                    className="relative w-9 h-9 flex items-center justify-center cursor-default"
                                    aria-label={`${t("Level_level")} ${level}`}
                                >
                                    <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36">
                                        <circle
                                            cx="18"
                                            cy="18"
                                            r={radius}
                                            fill="none"
                                            strokeWidth="2.5"
                                            className="stroke-gray-200 dark:stroke-bgDark-300"
                                        />
                                        <circle
                                            cx="18"
                                            cy="18"
                                            r={radius}
                                            fill="none"
                                            strokeWidth="2.5"
                                            strokeDasharray={circumference}
                                            strokeDashoffset={offset}
                                            strokeLinecap="round"
                                            className="stroke-primary transition-[stroke-dashoffset] duration-500"
                                        />
                                    </svg>
                                    <span className="text-[10px] font-bold text-primary">
                                        {level}
                                    </span>
                                </div>
                                <span
                                    role="tooltip"
                                    className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md bg-gray-900 dark:bg-grayDark-700 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 shadow-lg"
                                >
                                    {t("Level_level")} {level} · {progressToNextLevel}%
                                </span>
                            </div>
                        );
                    })()}
                </div>
            ) : (
            <div className="relative">
                {session &&
                    !sessionLoading &&
                    session.user &&
                    session.user.currentTeam && (
                        <div className="px-3 mb-3">
                            <TeamSwitcher
                                currentTeam={session.user.currentTeam}
                                availableTeams={
                                    session.user.availableTeams || []
                                }
                                showCreateTeam={false}
                                size="default"
                                useModal
                                fullWidth
                            />
                        </div>
                    )}
                {session &&
                    !sessionLoading &&
                    session.user &&
                    !session.user.pro &&
                    !session.user.essential &&
                    !session.user.currentTeam && (
                        <div className="px-3">
                            {/* Premium */}
                            <Link
                                href="/pricing"
                                className="relative group block"
                            >
                                {/* Badge de réduction flottant */}
                                {PROMO_CONFIG.isActive && (
                                    <div className="absolute -top-2 -right-2 z-10 bg-tertiary text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg transform rotate-12">
                                        -{PROMO_CONFIG.discount}%
                                    </div>
                                )}

                                {/* Même charte que le bouton Pro de la topbar
                                    (pilule orange, effet 3D maison) */}
                                <div className="px-4 py-2 rounded-full text-sm font-medium bg-primary text-white hover:bg-primary/90 duration-150 whitespace-nowrap btn-3d lip-orange text-center">
                                    <div className="flex items-center justify-center">
                                        <span>
                                            {PROMO_CONFIG.isActive
                                                ? PROMO_CONFIG.buttonText
                                                : t("SideHeader_upgradeButton")}
                                        </span>
                                    </div>
                                </div>

                                {PROMO_CONFIG.isActive && (
                                    <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                        <div className="bg-slate-800 dark:bg-grayDark-700 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap relative">
                                            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-slate-800 dark:border-b-grayDark-700"></div>
                                            {(locale === "en" ? PROMO_CONFIG.nameEn : PROMO_CONFIG.nameFr) || PROMO_CONFIG.text} · -{PROMO_CONFIG.discount}%
                                        </div>
                                    </div>
                                )}
                            </Link>

                        </div>
                    )}
                {/* New version */}
                {/* <Link
                    href="https://believemy.com/go/sZHSnA"
                    target="_blank"
                >
                    <div className="rounded-full bg-black/90 text-white px-3 py-1 inline-block mx-3 md:text-sm">
                        ✨ {t("SideHeader_newVersionTitle")}
                    </div>
                    <div className="mt-1 md:text-xs text-sm text-lightBlue bg-black/80 px-1.5 py-1 mx-3 rounded-b-xl rounded-tr-xl">
                        {t("SideHeader_newVersionDescription")}
                    </div>
                </Link> */}

                {/* Experience */}
                {sessionLoading ? (
                    <div className="m-3">
                        <Skeleton height={30} />
                    </div>
                ) : (
                    <>
                        {session ? (
                            <>
                            {session.user && (
                                <div className="mx-3 mb-1">
                                    <FeatureSuggestion />
                                </div>
                            )}
                            <div className="m-3 border-t dark:border-borderDark py-5">
                                <Level session={session} integratedOnSidebar />
                            </div>
                            </>
                        ) : (
                            <div className="m-3 border-t dark:border-borderDark pt-5 flex flex-col gap-2.5">
                                <Link href="/signup">
                                    <Button type="pop" className="w-full" small>
                                        {t("SideHeader_createAccountButton")}
                                    </Button>
                                </Link>
                                <Link href="/login">
                                    <Button
                                        type="secondary"
                                        className="w-full"
                                        small
                                    >
                                        {t("SideHeader_loginButton")}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </>
                )}

                {/* Options */}
                <div className="relative">
                    <ContactModal
                        isOpen={contactIsOpen}
                        onClose={() => setContactIsOpen(false)}
                        user={!sessionLoading ? session?.user : null}
                    />
                    {/* Help Dropdown */}
                    {helpModalIsOpen && (
                        <div className="absolute bottom-full left-0 right-0 mx-3 z-50">
                            <div className="bg-white dark:bg-bgDark-840 rounded-lg shadow-lg border border-gray-200 dark:border-borderDark overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setHelpModalIsOpen(false);
                                        setContactIsOpen(true);
                                    }}
                                    className="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/10 transition-colors"
                                >
                                    <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-3.5 h-3.5 text-primary"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                            <polyline points="22,6 12,13 2,6"></polyline>
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-0">
                                            {t("SideHeader_needHelp")}
                                        </p>
                                    </div>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3.5 h-3.5 text-gray-400"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </button>
                            </div>
                            {/* Arrow */}
                            <div className="flex justify-start pl-[25%]">
                                <div className="w-3 h-3 bg-white dark:bg-bgDark-840 border-r border-b border-gray-200 dark:border-borderDark transform rotate-45 -mt-1.5"></div>
                            </div>
                        </div>
                    )}
                    {/* Languages Dropdown */}
                    {languagesModalIsOpen && (
                        <div className="absolute bottom-full left-0 right-0 mx-3 z-50">
                            <div className="bg-white dark:bg-bgDark-840 rounded-lg shadow-lg border border-gray-200 dark:border-borderDark overflow-hidden">
                                <div className="p-1">
                                    {availableLanguages.map((lang) => (
                                        <div
                                            key={lang.id}
                                            onClick={() =>
                                                updateLanguage(lang.id)
                                            }
                                            className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                                                locale === lang.id
                                                    ? "bg-primary/10 text-primary"
                                                    : "hover:bg-gray-50 dark:hover:bg-white/10 text-gray-700 dark:text-white"
                                            }`}
                                        >
                                            <span className="text-sm font-medium">
                                                {lang.label}
                                            </span>
                                            {locale === lang.id && (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-4 h-4 ml-auto"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {/* Arrow */}
                            <div className="flex justify-end pr-[25%]">
                                <div className="w-3 h-3 bg-white dark:bg-bgDark-840 border-r border-b border-gray-200 dark:border-borderDark transform rotate-45 -mt-1.5"></div>
                            </div>
                        </div>
                    )}
                    <div className="flex border-t dark:border-borderDark">
                        {/* Help */}
                        <div
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm cursor-pointer transition-all duration-150 border-b-2 ${
                                helpModalIsOpen
                                    ? "border-primary text-primary dark:text-primary-100"
                                    : "border-transparent text-gray-500 dark:text-textDark/80 hover:text-gray-700 dark:hover:text-textDark"
                            }`}
                            onClick={() => {
                                setLanguagesModalIsOpen(false);
                                setHelpModalIsOpen(!helpModalIsOpen);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                            {t("SideHeader_needHelpButton")}
                        </div>

                        {/* Languages */}
                        <div
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm cursor-pointer transition-all duration-150 border-b-2 ${
                                languagesModalIsOpen
                                    ? "border-primary text-primary dark:text-primary-100"
                                    : "border-transparent text-gray-500 dark:text-textDark/80 hover:text-gray-700 dark:hover:text-textDark"
                            }`}
                            onClick={() => {
                                setHelpModalIsOpen(false);
                                setLanguagesModalIsOpen(!languagesModalIsOpen);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="2" y1="12" x2="22" y2="12"></line>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                            </svg>
                            {locale.toUpperCase()}
                        </div>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}
